import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Outlet, useLocation } from "react-router-dom";
import { m as motion, useReducedMotion } from "framer-motion";
import { LogOut, RefreshCcw, X } from "lucide-react";

import { ThemeToggle } from "@/components/ThemeToggle";
import { HelpGuideDialog, useFirstTimeHelp } from "@/components/HelpGuideDialog";
import { SyncStatusBadge } from "@/components/SyncStatus";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getFieldNoteCount, getMaintenanceBanner, getPunchNotesReviewCount, type MaintenanceBanner } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useStorageEstimate } from "@/hooks/useStorageEstimate";
import { DashboardNav } from "@/pages/dashboard/DashboardNav";
import { EmployeeSubNav } from "@/pages/dashboard/EmployeeSubNav";
import { useDashboardRoles } from "@/pages/dashboard/useDashboardRoles";

import { clearAllSyncedData } from "@/lib/outbox";
import { fmtDateTime } from "@/lib/time";

export function DashboardPage() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const user = auth.user;
  const online = useOnlineStatus();
  const { est: storageEst, refresh: refreshStorage } = useStorageEstimate();
  const pathname = useLocation().pathname;
  const { canReviewFieldNotes, canViewFieldNotesAdmin } = useDashboardRoles();
  const prefersReducedMotion = useReducedMotion();
  const { hasSeen, markSeen } = useFirstTimeHelp();
  const [helpOpen, setHelpOpen] = React.useState(false);
  const [clearSyncedOpen, setClearSyncedOpen] = React.useState(false);
  const [dismissedBannerId, setDismissedBannerId] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (!hasSeen) {
      setHelpOpen(true);
    }
  }, [hasSeen]);

  const { data: maintenanceBannerData } = useQuery({
    queryKey: ["maintenance-banner"],
    queryFn: getMaintenanceBanner,
    refetchInterval: 5 * 60 * 1000,
  });

  const { data: unprocessedFieldNotesCountData } = useQuery({
    queryKey: ["field-notes-count", "unprocessed"],
    queryFn: () => getFieldNoteCount({ processed: false, all: true }),
    enabled: !!user && canViewFieldNotesAdmin,
  });
  const { data: punchNotesReviewCountData } = useQuery({
    queryKey: ["punch-notes-review-count"],
    queryFn: () => getPunchNotesReviewCount(),
    enabled: !!user && canViewFieldNotesAdmin,
  });
  const unackedNotesTotal =
    (unprocessedFieldNotesCountData?.count ?? 0) + (punchNotesReviewCountData?.count ?? 0);

  const visibleBanner: MaintenanceBanner | null = maintenanceBannerData ?? null;
  const showBanner = visibleBanner && visibleBanner.id !== dismissedBannerId;

  return (
    <main className="min-h-screen w-full px-4 py-6 md:px-8" data-testid="dashboard">
      <div className="w-full space-y-4">
        {showBanner && (
          <Alert className="relative border-warning/20 bg-warning/10 text-black">
            <AlertTitle className="pr-8">Scheduled Maintenance</AlertTitle>
            <div className="text-sm">
              {visibleBanner.message}
              {(visibleBanner.maintenance_starts_at || visibleBanner.maintenance_ends_at) && (
                <span className="block text-sm opacity-90">
                  {visibleBanner.maintenance_starts_at && `Starts: ${fmtDateTime(visibleBanner.maintenance_starts_at, "America/Detroit").replace("America/Detroit", "ET")}`}
                  {visibleBanner.maintenance_starts_at && visibleBanner.maintenance_ends_at && " — "}
                  {visibleBanner.maintenance_ends_at && `Ends: ${fmtDateTime(visibleBanner.maintenance_ends_at, "America/Detroit").replace("America/Detroit", "ET")}`}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setDismissedBannerId(visibleBanner.id)}
              className="absolute right-3 top-3 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              aria-label="Dismiss maintenance banner"
            >
              <X className="size-4" />
            </button>
          </Alert>
        )}
        <motion.header
          className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between"
          initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <img src="/images/Synergy_Logo.png" alt="Synergy Fiber" className="h-8 md:h-10 dark:hidden" />
              <img src="/images/Synergy_Logo_dark.png" alt="Synergy Fiber" className="h-8 md:h-10 hidden dark:block" />
              <h1 className="text-l font-semibold tracking-normal md:text-l">Dispatch Time Tracking</h1>
            </div>
            <p className="text-sm text-muted-foreground">
              Employee clock-in/out, GPS, photo verification, admin reporting, charts, and CSV export.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={online ? "default" : "destructive"}>{online ? "Online" : "Offline"}</Badge>
            <SyncStatusBadge userId={user?.id} />
            {storageEst && (
              <Badge
                variant={storageEst.isLow ? "destructive" : "outline"}
                className="text-[10px] tabular-nums cursor-help"
                title={`Browser origin storage (IndexedDB + query cache). Synced outbox items kept ~3 days (auto), or cleared immediately via 'Clear synced' (also drops cached dropdowns). Meter is approximate.`}
                onClick={() => refreshStorage()}
              >
                Storage: {(storageEst.usage / 1024 / 1024).toFixed(1)} / {(storageEst.quota / 1024 / 1024).toFixed(0)} MB ({storageEst.percent}%){storageEst.isLow ? " ⚠" : ""}
              </Badge>
            )}
            {storageEst && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-1.5 text-[10px] text-muted-foreground hover:text-destructive"
                onClick={() => setClearSyncedOpen(true)}
                title="Clear all synced records + query cache from local storage. Large photos already freed on sync success. Does not touch pending items. Requires confirmation."
              >
                Clear synced
              </Button>
            )}
            <Badge variant="outline">{user?.role}</Badge>
            <Button
              variant="outline"
              onClick={() => {
                queryClient.invalidateQueries({ queryKey: ["cost-codes"] });
                queryClient.invalidateQueries({ queryKey: ["customers"] });
                queryClient.invalidateQueries({ queryKey: ["entries"] });
                queryClient.invalidateQueries({ queryKey: ["recent-entries"] });
                queryClient.invalidateQueries({ queryKey: ["field-notes"] });
                queryClient.invalidateQueries({ queryKey: ["field-notes-count"] });
                queryClient.invalidateQueries({ queryKey: ["field-notes-review"] });
                queryClient.invalidateQueries({ queryKey: ["punch-notes-review"] });
                queryClient.invalidateQueries({ queryKey: ["punch-notes-review-count"] });
              }}
            >
              <RefreshCcw className="size-4" />
              Sync
            </Button>
            <HelpGuideDialog open={helpOpen} onOpenChange={(v) => { setHelpOpen(v); if (!v) markSeen(); }} />
            <ThemeToggle />
            <Button variant="ghost" onClick={auth.logout}>
              <LogOut className="size-4" />
              Sign out
            </Button>
          </div>
        </motion.header>

        <DashboardNav unackedNotesTotal={unackedNotesTotal} canReviewFieldNotes={canReviewFieldNotes} />
        {pathname.startsWith("/employee") && <EmployeeSubNav />}

        <Outlet />
      </div>

      <Dialog open={clearSyncedOpen} onOpenChange={setClearSyncedOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clear synced data from local storage?</DialogTitle>
            <DialogDescription>
              This will permanently remove all <strong>synced</strong> records from this browser's IndexedDB (punches and field notes that have already been uploaded to the server).
              Large photo blobs are released immediately on successful sync; this step removes the remaining small metadata rows and the locally cached lists/recent entries.
              Pending, failed, or unsynced items will <strong>not</strong> be deleted.
              The browser storage meter is approximate and may not update instantly (reload or wait a moment if needed).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClearSyncedOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                try {
                  await clearAllSyncedData();
                  await refreshStorage();
                  setTimeout(() => { void refreshStorage(); }, 120);
                } catch {}
                setClearSyncedOpen(false);
              }}
            >
              Clear synced data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
